<script lang="ts">
  import { popup, type PopupSettings } from '@skeletonlabs/skeleton'
  import { onMount, untrack } from 'svelte'
  import type { ChangeEventHandler } from 'svelte/elements'

  import {
    displayStorageTypes,
    getPattern,
    type IGitRepo,
    isGCSField,
    isGit,
    isGitField,
    isHuggingFaceField,
    isS3Field,
    isSelectField,
    isSpecialField,
    isTextField,
    type IStorageConfig,
    type IStorageGit,
    type ITextField,
    storageFields,
    StorageType,
    userVisibleStorageTypeValues,
  } from './storage-configs-store'
  import InvalidHoverInfo from '@components/InvalidHoverInfo.svelte'
  import InvalidIcon from '@components/InvalidIcon.svelte'
  import ReadOnly from '@components/ReadOnly.svelte'
  import RequiredNameField from '@components/RequiredNameField.svelte'
  import Select from '@components/Select.svelte'
  import {
    organizationAsyncState,
    organizationsStore,
  } from '@routes/organizations/organizations-store'
  import { loggedInStore } from '@stores/logged-in-store'
  import { regexValidator, ValidityState } from '@utils/validation.svelte'

  interface Props {
    storageFormElement?: HTMLFormElement
    storageConfig?: IStorageConfig | null
    selectedStorageType?: StorageType | null
    storageConfigName?: string
    inputValues?: { [key: string]: string | undefined | null }
    currentOrganizationId?: number
    readOnly?: boolean
    creating?: 'dataset' | 'model'
  }

  let {
    storageFormElement = $bindable(),
    storageConfig = null,
    selectedStorageType = $bindable(storageConfig?.type || null),
    storageConfigName = $bindable(storageConfig?.name || ''),
    inputValues = $bindable({}),
    currentOrganizationId = $bindable($loggedInStore.primaryOrganizationId),
    readOnly = $bindable(false),
    creating,
  }: Props = $props()

  const invalids: Record<string, ValidityState> = $state({
    name: new ValidityState(),
  })

  const getFromGitRepo = (fieldName: string) => {
    if (fieldName.includes('repo.')) {
      const innerKey = fieldName.split('repo.')[1] as keyof IGitRepo
      return storageConfig?.git?.repo[innerKey] as string
    }
    return storageConfig?.git?.[fieldName as keyof IStorageGit] as string
  }

  onMount(() => {
    if (!storageConfig) {
      return
    }
    const storageType = storageConfig.type
    selectedStorageType = storageType

    storageFields[storageType].forEach((field) => {
      const fieldName = field.fieldName
      switch (storageType) {
        case StorageType.AWS_S3:
        case StorageType.S3_COMPATIBLE: {
          if (isS3Field(storageConfig?.s3, storageType, fieldName)) {
            inputValues[fieldName] = storageConfig.s3?.[fieldName]
          }
          break
        }
        case StorageType.GCP: {
          if (isGCSField(storageConfig?.gcp, storageType, fieldName)) {
            inputValues[fieldName] = storageConfig.gcp?.[fieldName]
          }
          break
        }
        case StorageType.Git: {
          if (isGitField(storageConfig?.git, storageType, fieldName)) {
            inputValues[fieldName] = getFromGitRepo(fieldName)
          }
          break
        }
        case StorageType.HuggingFace: {
          if (isHuggingFaceField(storageConfig?.huggingface, storageType, fieldName)) {
            const value = storageConfig.huggingface?.[fieldName]
            inputValues[fieldName] = value?.toString()
            if (fieldName === 'datasetId') {
              inputValues['type'] = 'dataset'
            } else if (fieldName === 'modelId') {
              inputValues['type'] = 'model'
            }
          }
          inputValues['tokenId'] = storageConfig.huggingface?.tokenId?.toString()
          inputValues['authType'] =
            storageConfig.huggingface?.authType ??
            (storageConfig.huggingface?.tokenId ? 'token' : 'none')
          inputValues['saveToken'] = null
          inputValues['tokenName'] = ''
          break
        }
        case StorageType.Local: {
          // Local storage does not (yet) have any fields
          break
        }
        default: {
          const exhaustiveCheck: never = storageType
          return exhaustiveCheck
        }
      }
    })
    if (storageConfig.s3) {
      // Leave the secretAccessKey empty for security reasons
      inputValues['secretAccessKey'] = ''
    }
  })

  const onStorageTypeChange: ChangeEventHandler<HTMLSelectElement> = (event) => {
    const nextStorageTypeValue = event.currentTarget.value
    if (!nextStorageTypeValue) {
      selectedStorageType = null
      return
    }
    selectedStorageType = nextStorageTypeValue as StorageType
    if (selectedStorageType) {
      storageFields[selectedStorageType]?.forEach((field) => {
        if (isSelectField(field)) {
          inputValues[field.fieldName] = field.getInitialSelectedOptionValue(inputValues, creating)
        } else if (isTextField(field)) {
          inputValues[field.fieldName] = field.default || ''
        } else {
          inputValues[field.fieldName] = ''
        }
        if (isTextField(field) && field.pattern) {
          invalids[field.fieldName] = new ValidityState()
        }
      })
      if (isGit(storageConfig, selectedStorageType)) {
        inputValues['repo.authType'] = 'none'
      }
      if (selectedStorageType === StorageType.HuggingFace) {
        inputValues['tokenId'] = undefined
        inputValues['saveToken'] = null
        inputValues['tokenName'] = ''
      }
    }
  }

  const onLoadHandler = (fieldName: string) => (e: ProgressEvent<FileReader>) => {
    try {
      inputValues[fieldName] = JSON.parse(e.target?.result?.toString() || '')
    } catch (error) {
      console.error('Failed to parse JSON file', error)
    }
  }

  const handleFileUpload =
    (fieldName: string) => (event: Event & { currentTarget: HTMLInputElement }) => {
      const file = event.currentTarget.files?.[0]
      if (file) {
        const reader = new FileReader()
        reader.onload = onLoadHandler(fieldName)
        reader.readAsText(file)
      }
    }

  const disabledEditStorageTypePopup: PopupSettings = {
    event: 'hover',
    placement: 'right',
    target: 'disabledEditStorageType',
  }

  const getPatternValidator = (field: ITextField) => {
    return field.pattern && field.patternError
      ? regexValidator(invalids[field.fieldName], field.patternError)
      : () => {}
  }

  let lastShownFields = $state(new Set<string>())
  $effect(() => {
    if (!selectedStorageType) {
      return
    }
    const shownFields = new Set(
      storageFields[selectedStorageType]
        ?.filter((field) => field.showField && field.showField(inputValues, creating))
        .map((field) => field.fieldName),
    )
    const untrackedLastShownFields = untrack(() => lastShownFields)
    for (const field of untrackedLastShownFields) {
      if (!shownFields.has(field)) {
        inputValues[field] = undefined
        console.log('Clearing state for hidden field', field)
        // Clear state for hidden fields
      }
    }
    if (shownFields && shownFields.size > 0) {
      lastShownFields = shownFields
    }
  })
</script>

{#snippet invalidIcon(field: keyof typeof invalids, value?: string | null)}
  <InvalidIcon {invalids} {field} {value}></InvalidIcon>
{/snippet}
{#snippet invalidHoverInfo(field: keyof typeof invalids)}
  <InvalidHoverInfo {invalids} {field}></InvalidHoverInfo>
{/snippet}

<form
  bind:this={storageFormElement}
  class="w-full max-w-full space-y-2 {readOnly ? 'pb-4' : 'pb-2'}"
>
  <RequiredNameField
    id="storage-name"
    label="Storage config name:"
    {invalids}
    field="name"
    bind:value={storageConfigName}
    {readOnly}
  />
  <label for="storage-organization-to-associate-with">Associated organization:</label>
  {#if readOnly}
    <ReadOnly value={$organizationsStore.get(currentOrganizationId)?.name} />
  {:else if $organizationAsyncState.loading}
    <p>Loading organizations...</p>
  {:else if $organizationAsyncState.error}
    <p>{$organizationAsyncState.error}</p>
  {:else}
    <Select
      required
      id="storage-organization-to-associate-with"
      name="storage-organization-to-associate-with"
      bind:value={currentOrganizationId}
    >
      <option selected value>Select an organization</option>
      {#each $organizationsStore as [_id, organization] (organization.id)}
        <option value={organization.id} selected={organization.id === currentOrganizationId}>
          {organization.name}
        </option>
      {/each}
    </Select>
  {/if}
  <label for="storage-type">Storage type:</label>
  {#if readOnly}
    <ReadOnly value={selectedStorageType ? displayStorageTypes[selectedStorageType] : ''} />
  {:else}
    <span use:popup={disabledEditStorageTypePopup}>
      <div
        class={`card variant-filled-warning z-50 p-2 ${storageConfig ? 'visible' : 'invisible'}`}
        data-popup="disabledEditStorageType"
      >
        <p>You cannot change storage type while editing</p>
        <div class="variant-filled-warning arrow"></div>
      </div>
      <Select
        required
        id="storage-type"
        name="storage-type"
        value={selectedStorageType ?? ''}
        placeholder="Select Type"
        disabled={!!storageConfig}
        onchange={onStorageTypeChange}
      >
        <option value="" disabled>Select type</option>
        {#each userVisibleStorageTypeValues as storageType (storageType)}
          {#if window.airGapped || storageType !== StorageType.Local}
            <option value={storageType}>{displayStorageTypes[storageType]}</option>
          {/if}
        {/each}
      </Select>
    </span>
  {/if}
  {#if selectedStorageType}
    {#each storageFields[selectedStorageType] as field (field.fieldName)}
      {@const showField = field.showField === undefined || field.showField(inputValues, creating)}
      {@const showFieldInReadOnly = showField && field.type !== 'password' && field.type !== 'file'}
      {#if readOnly ? showFieldInReadOnly : showField}
        <div class="relative space-y-2 pl-4">
          {#if isSpecialField(field)}
            {@const FieldComponent = field.component}
            <FieldComponent bind:inputValues />
          {:else}
            <label for={field.fieldName}>
              {field.label}
            </label>
            {#if readOnly}
              {#if field.fieldName === 'repo.authType'}
                <ReadOnly value={inputValues[field.fieldName] || 'None'} />
              {:else}
                <ReadOnly value={inputValues[field.fieldName]} />
              {/if}
            {:else if field.type === 'file'}
              <input
                class="input my-0 box-border flex w-fit px-2 py-1 md:min-w-96"
                type="file"
                id={field.fieldName}
                name={field.fieldName}
                accept={field.accept}
                onchange={handleFileUpload(field.fieldName)}
                required={field.required}
              />
            {:else if isTextField(field) && field.fieldName === 'bucketUrl'}
              <div class="input input-group input-group-divider grid-cols-[auto_1fr_auto]">
                <div class="input-group-shim">s3://</div>
                <input
                  class="input my-0 box-border flex w-fit px-2 py-1 md:min-w-96"
                  type="text"
                  id="bucketUrl"
                  name="bucketUrl"
                  value={(inputValues.bucketUrl || '').replace('s3://', '')}
                  oninput={(e) => (inputValues.bucketUrl = 's3://' + e.currentTarget.value)}
                  oninvalid={getPatternValidator(field)}
                  onchange={getPatternValidator(field)}
                  required={field.required}
                  pattern={getPattern(field)}
                  placeholder="my-bucket-name"
                />
                {@render invalidIcon('bucketUrl', inputValues.bucketUrl)}
              </div>
              {@render invalidHoverInfo('bucketUrl')}
            {:else if field.type === 'select'}
              <Select
                id={field.fieldName}
                name={field.fieldName}
                bind:value={inputValues[field.fieldName]}
                required={field.required}
              >
                {#each Object.entries(field.options) as [optionValue, optionName] (optionValue)}
                  <option value={optionValue}>{optionName}</option>
                {/each}
              </Select>
            {:else}
              {#snippet inputField()}
                <input
                  class="autocomplete input my-0 box-border flex w-fit px-2 py-1 md:min-w-96"
                  type={field.type}
                  id={field.fieldName}
                  name={field.fieldName}
                  value={inputValues[field.fieldName]}
                  oninput={(e) => {
                    inputValues[field.fieldName] = e.currentTarget?.value
                  }}
                  oninvalid={getPatternValidator(field)}
                  onchange={getPatternValidator(field)}
                  autocomplete={field.autocomplete}
                  required={field.required}
                  pattern={getPattern(field)}
                />
              {/snippet}
              {#if field.pattern}
                <div class="input-group input-group-divider grid-cols-[1fr_auto]">
                  {@render inputField()}
                  {@render invalidIcon(field.fieldName, storageConfigName)}
                </div>
                {@render invalidHoverInfo(field.fieldName)}
              {:else}
                {@render inputField()}
              {/if}
            {/if}
          {/if}
        </div>
      {/if}
    {/each}
  {/if}
</form>
